/**
 * IPTransferTab — IN REVIEW stage
 * IP protection, FTO, transfer mechanism, timeline
 */

import { useState } from 'react'
import type { ProjectDetailProject } from '../../../types'
import { InlineNote } from '../../ui/InlineNote'
import { CompletableSection } from '../../ui/CompletableSection'
import type { SectionCompletionState } from '../../../hooks/useSectionCompletions'

export interface TabContentProps {
  project: ProjectDetailProject
  projectId: number
  domain: string
  onFieldChange: (field: string, value: any) => void
  sectionCompletions?: SectionCompletionState
  tabId?: string
}

const KNOW_HOW_TYPES = [
  'Scientific Discovery',
  'Engineering Innovation',
  'Proprietary Data',
  'Unique Bio Asset',
  'Process Know-How',
  'Domain Expertise',
]

const IP_OWNERSHIP_OPTIONS = [
  { value: 'University', label: 'University Owned' },
  { value: 'Researcher', label: 'Researcher Owned' },
  { value: 'Joint', label: 'Joint Ownership' },
  { value: 'Company', label: 'Company Owned' },
]

const FTO_STATUS_OPTIONS = [
  { value: 'Clear', label: 'Clear' },
  { value: 'Under Review', label: 'Under Review' },
  { value: 'Blocked', label: 'Blocked' },
  { value: 'Not Assessed', label: 'Not Assessed' },
]

const IP_TERRITORY_OPTIONS = [
  { value: 'US', label: 'United States' },
  { value: 'EU', label: 'European Union' },
  { value: 'Global', label: 'Global' },
  { value: 'Other', label: 'Other' },
]

const TRANSFER_MECHANISM_OPTIONS = [
  { value: 'License', label: 'License Agreement' },
  { value: 'Assignment', label: 'Full Assignment' },
  { value: 'Option', label: 'Option Agreement' },
  { value: 'Exclusive', label: 'Exclusive License' },
]

const TTO_COOPERATION_OPTIONS = [
  { value: 'Supportive', label: 'Supportive' },
  { value: 'Neutral', label: 'Neutral' },
  { value: 'Resistant', label: 'Resistant' },
  { value: 'Unknown', label: 'Unknown' },
]

const IP_COOWNERSHIP_OPTIONS = [
  { value: 'No', label: 'No Co-ownership' },
  { value: 'Yes', label: 'Yes, other entities' },
  { value: 'Likely', label: 'Likely' },
]

const TRANSFER_TIMELINE_OPTIONS = [
  { value: '0-3', label: '0-3 months' },
  { value: '3-6', label: '3-6 months' },
  { value: '6-12', label: '6-12 months' },
  { value: '12+', label: '12+ months' },
]

export function IPTransferTab({
  project,
  projectId,
  domain,
  onFieldChange,
  sectionCompletions: sc,
  tabId = 'tab-ip-transfer',
}: TabContentProps) {
  const isComplete = (sectionId: string) => sc?.isSectionComplete(tabId, sectionId) ?? false
  const getInfo = (sectionId: string) => sc?.getCompletionInfo(tabId, sectionId)
  const toggle = (sectionId: string) => sc?.toggleSection(tabId, sectionId)

  const [selectedKnowHow, setSelectedKnowHow] = useState<string[]>([])

  const handleKnowHowToggle = (type: string) => {
    const updated = selectedKnowHow.includes(type)
      ? selectedKnowHow.filter((t) => t !== type)
      : [...selectedKnowHow, type]
    setSelectedKnowHow(updated)
    onFieldChange('know_how_types', updated)
  }

  return (
    <div className="space-y-6">
      {/* Core Know-How */}
      <CompletableSection
        sectionId="ip-core-knowhow"
        title="Core Know-How"
        isComplete={isComplete('ip-core-knowhow')}
        completedByName={getInfo('ip-core-knowhow')?.completed_by_name}
        completedAt={getInfo('ip-core-knowhow')?.completed_at}
        onToggle={() => toggle('ip-core-knowhow')}
      >
        <div className="mb-4">
          <label className="text-[13px] font-medium text-[#e8e8f0] block mb-2">
            Description
          </label>
          <InlineNote
            value={project.core_knowhow || ''}
            onChange={(value) => onFieldChange('core_knowhow', value)}
            placeholder="What is the core technical knowledge?"
            minHeight={100}
          />
        </div>

        <div>
          <label className="text-[13px] font-medium text-[#e8e8f0] block mb-3">
            Know-How Type
          </label>
          <div className="space-y-2">
            {KNOW_HOW_TYPES.map((type) => (
              <label key={type} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedKnowHow.includes(type)}
                  onChange={() => handleKnowHowToggle(type)}
                  className="w-4 h-4 rounded border-[#2a2a44]"
                />
                <span className="text-[13px] text-[#e8e8f0]">{type}</span>
              </label>
            ))}
          </div>
        </div>
      </CompletableSection>

      {/* IP Protection */}
      <CompletableSection
        sectionId="ip-protection"
        title="IP Protection"
        isComplete={isComplete('ip-protection')}
        completedByName={getInfo('ip-protection')?.completed_by_name}
        completedAt={getInfo('ip-protection')?.completed_at}
        onToggle={() => toggle('ip-protection')}
      >
        <div className="mb-4">
          <label className="text-[13px] font-medium text-[#e8e8f0] block mb-2">
            Protection Status
          </label>
          <input
            type="text"
            placeholder="e.g. Patented, Pending, Trade Secret"
            className="
              w-full px-3 py-2
              bg-[#12122a] border border-[#2a2a44]
              rounded-lg
              text-[#e8e8f0] text-[13px]
              placeholder:text-[#5a5a6c]
              focus:border-[#3a3a54] focus:ring-1 focus:ring-[#3a3a54]/50
            "
          />
        </div>

        <div className="mb-4">
          <label className="text-[13px] font-medium text-[#e8e8f0] block mb-2">
            IP Ownership
          </label>
          <select
            className="
              w-full px-3 py-2
              bg-[#12122a] border border-[#2a2a44]
              rounded-lg
              text-[#e8e8f0] text-[13px]
              focus:border-[#3a3a54] focus:ring-1 focus:ring-[#3a3a54]/50
            "
          >
            <option value="">Select ownership</option>
            {IP_OWNERSHIP_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-4">
          <label className="text-[13px] font-medium text-[#e8e8f0] block mb-2">
            Patent Numbers
          </label>
          <input
            type="text"
            placeholder="e.g. US 10,123,456 | EP 3,456,789"
            className="
              w-full px-3 py-2
              bg-[#12122a] border border-[#2a2a44]
              rounded-lg
              text-[#e8e8f0] text-[13px]
              placeholder:text-[#5a5a6c]
              focus:border-[#3a3a54] focus:ring-1 focus:ring-[#3a3a54]/50
            "
          />
        </div>

        <div>
          <label className="text-[13px] font-medium text-[#e8e8f0] block mb-2">
            IP Territory
          </label>
          <select
            className="
              w-full px-3 py-2
              bg-[#12122a] border border-[#2a2a44]
              rounded-lg
              text-[#e8e8f0] text-[13px]
              focus:border-[#3a3a54] focus:ring-1 focus:ring-[#3a3a54]/50
            "
          >
            <option value="">Select territory</option>
            {IP_TERRITORY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </CompletableSection>

      {/* Freedom to Operate (FTO) */}
      <CompletableSection
        sectionId="ip-fto"
        title="Freedom to Operate (FTO)"
        isComplete={isComplete('ip-fto')}
        completedByName={getInfo('ip-fto')?.completed_by_name}
        completedAt={getInfo('ip-fto')?.completed_at}
        onToggle={() => toggle('ip-fto')}
      >
        <div className="mb-4">
          <label className="text-[13px] font-medium text-[#e8e8f0] block mb-2">
            FTO Status
          </label>
          <select
            className="
              w-full px-3 py-2
              bg-[#12122a] border border-[#2a2a44]
              rounded-lg
              text-[#e8e8f0] text-[13px]
              focus:border-[#3a3a54] focus:ring-1 focus:ring-[#3a3a54]/50
            "
          >
            <option value="">Select status</option>
            {FTO_STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-4">
          <label className="text-[13px] font-medium text-[#e8e8f0] block mb-2">
            FTO Analyst / Firm
          </label>
          <input
            type="text"
            placeholder="Name of analyst or law firm"
            className="
              w-full px-3 py-2
              bg-[#12122a] border border-[#2a2a44]
              rounded-lg
              text-[#e8e8f0] text-[13px]
              placeholder:text-[#5a5a6c]
              focus:border-[#3a3a54] focus:ring-1 focus:ring-[#3a3a54]/50
            "
          />
        </div>

        <div className="mb-4">
          <label className="text-[13px] font-medium text-[#e8e8f0] block mb-2">
            Territory
          </label>
          <select
            className="
              w-full px-3 py-2
              bg-[#12122a] border border-[#2a2a44]
              rounded-lg
              text-[#e8e8f0] text-[13px]
              focus:border-[#3a3a54] focus:ring-1 focus:ring-[#3a3a54]/50
            "
          >
            <option value="">Select territory</option>
            {IP_TERRITORY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-4">
          <label className="text-[13px] font-medium text-[#e8e8f0] block mb-2">
            Blocking Patents
          </label>
          <input
            type="text"
            placeholder="Known blocking patents, if any"
            className="
              w-full px-3 py-2
              bg-[#12122a] border border-[#2a2a44]
              rounded-lg
              text-[#e8e8f0] text-[13px]
              placeholder:text-[#5a5a6c]
              focus:border-[#3a3a54] focus:ring-1 focus:ring-[#3a3a54]/50
            "
          />
        </div>

        <div>
          <label className="text-[13px] font-medium text-[#e8e8f0] block mb-2">
            FTO Notes
          </label>
          <InlineNote
            value={''}
            onChange={(value) => onFieldChange('fto_notes', value)}
            placeholder="Detailed FTO assessment and risks"
            minHeight={100}
          />
        </div>
      </CompletableSection>

      {/* IP Transfer Path */}
      <CompletableSection
        sectionId="ip-transfer-path"
        title="IP Transfer Path"
        isComplete={isComplete('ip-transfer-path')}
        completedByName={getInfo('ip-transfer-path')?.completed_by_name}
        completedAt={getInfo('ip-transfer-path')?.completed_at}
        onToggle={() => toggle('ip-transfer-path')}
      >
        <div className="mb-4">
          <label className="text-[13px] font-medium text-[#e8e8f0] block mb-2">
            Transfer Mechanism
          </label>
          <select
            className="
              w-full px-3 py-2
              bg-[#12122a] border border-[#2a2a44]
              rounded-lg
              text-[#e8e8f0] text-[13px]
              focus:border-[#3a3a54] focus:ring-1 focus:ring-[#3a3a54]/50
            "
          >
            <option value="">Select mechanism</option>
            {TRANSFER_MECHANISM_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-4">
          <label className="text-[13px] font-medium text-[#e8e8f0] block mb-2">
            TTO Cooperation
          </label>
          <select
            className="
              w-full px-3 py-2
              bg-[#12122a] border border-[#2a2a44]
              rounded-lg
              text-[#e8e8f0] text-[13px]
              focus:border-[#3a3a54] focus:ring-1 focus:ring-[#3a3a54]/50
            "
          >
            <option value="">Select cooperation level</option>
            {TTO_COOPERATION_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-4">
          <label className="text-[13px] font-medium text-[#e8e8f0] block mb-2">
            IP Co-ownership
          </label>
          <select
            className="
              w-full px-3 py-2
              bg-[#12122a] border border-[#2a2a44]
              rounded-lg
              text-[#e8e8f0] text-[13px]
              focus:border-[#3a3a54] focus:ring-1 focus:ring-[#3a3a54]/50
            "
          >
            <option value="">Select</option>
            {IP_COOWNERSHIP_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-4">
          <label className="text-[13px] font-medium text-[#e8e8f0] block mb-2">
            Existing Licenses
          </label>
          <input
            type="text"
            placeholder="Any existing license agreements"
            className="
              w-full px-3 py-2
              bg-[#12122a] border border-[#2a2a44]
              rounded-lg
              text-[#e8e8f0] text-[13px]
              placeholder:text-[#5a5a6c]
              focus:border-[#3a3a54] focus:ring-1 focus:ring-[#3a3a54]/50
            "
          />
        </div>

        <div className="mb-4">
          <label className="text-[13px] font-medium text-[#e8e8f0] block mb-2">
            Inventor Obligations
          </label>
          <input
            type="text"
            placeholder="Any restrictions on inventor involvement"
            className="
              w-full px-3 py-2
              bg-[#12122a] border border-[#2a2a44]
              rounded-lg
              text-[#e8e8f0] text-[13px]
              placeholder:text-[#5a5a6c]
              focus:border-[#3a3a54] focus:ring-1 focus:ring-[#3a3a54]/50
            "
          />
        </div>

        <div className="mb-4">
          <label className="text-[13px] font-medium text-[#e8e8f0] block mb-2">
            Transfer Timeline Estimate
          </label>
          <select
            className="
              w-full px-3 py-2
              bg-[#12122a] border border-[#2a2a44]
              rounded-lg
              text-[#e8e8f0] text-[13px]
              focus:border-[#3a3a54] focus:ring-1 focus:ring-[#3a3a54]/50
            "
          >
            <option value="">Select timeline</option>
            {TRANSFER_TIMELINE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-[13px] font-medium text-[#e8e8f0] block mb-2">
            Transfer Notes
          </label>
          <InlineNote
            value={''}
            onChange={(value) => onFieldChange('transfer_notes', value)}
            placeholder="Additional transfer considerations"
            minHeight={100}
          />
        </div>
      </CompletableSection>
    </div>
  )
}

export default IPTransferTab
